/**
 * Past events (`/me/history`).
 *
 * The same list as `/me/events`, reduced to what is already over: archived
 * events plus anything whose start time has passed. Both lists the API returns
 * are merged, because an organizer can also be a participant of their own event
 * and the archive must not show it twice.
 */

import { useTranslation } from 'react-i18next'

import type { EventSummaryDto } from '@shared/types'

import { PageHeader } from '../../components/layout/page-header'
import { ErrorBlock, SkeletonCard } from '../../components/layout/states'
import { EmptyState } from '../../components/ui/primitives'
import { useEventList } from '../../hooks/use-events'
import { EventRowCard } from '../../components/event/event-cards'
import { nowLocalDateTime } from '../../lib/format'

/** Archived, or started before the city's own current wall clock time. */
function isHistorical(event: EventSummaryDto, nowLocal: string): boolean {
  return event.status === 'archived' || event.eventDateStart < nowLocal
}

export function HistoryPage() {
  const { t } = useTranslation()
  const eventList = useEventList()

  // Every event time is the city's wall clock, so "now" is read there as well: a
  // browser in another zone would otherwise classify an outing as past or future by
  // its own clock.
  const nowLocal = nowLocalDateTime()
  const seen = new Set<string>()
  const historical: EventSummaryDto[] = []
  for (const event of [...(eventList.data?.created ?? []), ...(eventList.data?.joined ?? [])]) {
    if (seen.has(event.id) || !isHistorical(event, nowLocal)) {
      continue
    }
    seen.add(event.id)
    historical.push(event)
  }
  historical.sort((left, right) => right.eventDateStart.localeCompare(left.eventDateStart))

  return (
    <div className="container-page flex flex-col gap-6">
      <PageHeader
        breadcrumbs={[
          { label: t('nav.home'), to: '/' },
          { label: t('nav.me'), to: '/me' },
          { label: t('nav.history') },
        ]}
        title={t('nav.history')}
      />

      {eventList.isPending ? <SkeletonCard /> : null}

      {eventList.isError ? (
        <ErrorBlock
          title={t('error.internal_error')}
          retryLabel={t('common.retry')}
          onRetry={() => void eventList.refetch()}
        />
      ) : null}

      {eventList.data ? (
        historical.length === 0 ? (
          <EmptyState title={t('events.empty')} description={t('me.historyCardBody')} />
        ) : (
          <div className="grid gap-3">
            {historical.map((event) => (
              <EventRowCard key={event.id} event={event} />
            ))}
          </div>
        )
      ) : null}
    </div>
  )
}
